import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="admin--page-builder"
export default class extends Controller {
  static targets = [
    "item",
    "panel",
    "emptyState",
    "podName",
    "podType",
    "podPosition",
    "inspectorFrame",
    "addPodModal",
    "inspectorDrawer",
    "inspectorBackdrop"
  ]

  connect() {
    this.selectedPagePodId = null
    this.inspectorDirty = false

    this.handleInspectorFrameLoad = this.handleInspectorFrameLoad.bind(this)

    this.clearSelection()
  }

  inspectorFrameTargetConnected(element) {
    element.addEventListener("turbo:frame-load", this.handleInspectorFrameLoad)
  }

  inspectorFrameTargetDisconnected(element) {
    element.removeEventListener("turbo:frame-load", this.handleInspectorFrameLoad)
  }

  disconnect() {
    document.body.classList.remove("overflow-hidden")
  }

  select(event) {
    this.requestSelectItem(event.currentTarget)
  }

  itemTargetConnected(element) {
    if (element.dataset.autoselect === "true") {
      element.dataset.autoselect = "false"
      requestAnimationFrame(() => this.requestSelectItem(element))
      return
    }

    if (this.selectedPagePodId && element.dataset.id === this.selectedPagePodId) {
      requestAnimationFrame(() => {
        if (this.selectedPagePodId === element.dataset.id) {
          this.selectItem(element, { reloadInspector: false, preserveDirty: true })
        }
      })
    }
  }

  itemTargetDisconnected(element) {
    if (!this.selectedPagePodId || element.dataset.id !== this.selectedPagePodId) return

    requestAnimationFrame(() => {
      const replacement = this.itemTargets.find((item) => item.dataset.id === this.selectedPagePodId)
      if (replacement) {
        this.selectItem(replacement, { reloadInspector: false, preserveDirty: true })
        return
      }

      this.clearSelection()
    })
  }

  requestSelectItem(item) {
    const itemId = item?.dataset?.id
    const switchingItems = this.selectedPagePodId && this.selectedPagePodId !== itemId

    if (switchingItems && !this.confirmDiscardIfDirty()) return false
    if (switchingItems) this.resetInspectorDirtyState()

    this.selectItem(item)
    return true
  }

  selectItem(item, { reloadInspector = true, preserveDirty = false } = {}) {
    if (!item) return

    this.itemTargets.forEach((el) => {
      el.classList.remove("ring-2", "ring-primary", "border-primary")
    })

    item.classList.add("ring-2", "ring-primary", "border-primary")
    this.selectedPagePodId = item.dataset.id || null

    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasPanelTarget) this.panelTarget.classList.remove("hidden")

    if (this.hasPodNameTarget) this.podNameTarget.textContent = item.dataset.podName || "-"
    if (this.hasPodTypeTarget) this.podTypeTarget.textContent = item.dataset.podType || "-"
    if (this.hasPodPositionTarget) this.podPositionTarget.textContent = item.dataset.podPosition || "-"

    if (this.hasInspectorFrameTarget && item.dataset.podOverrideUrl && reloadInspector) {
      const currentSrc = this.inspectorFrameTarget.getAttribute("src")
      if (currentSrc !== item.dataset.podOverrideUrl) {
        this.inspectorFrameTarget.src = item.dataset.podOverrideUrl
      }
    }

    if (!preserveDirty) this.resetInspectorDirtyState()
    this.openInspectorDrawer()
  }

  openAddPodModal(event) {
    event.preventDefault()
    if (!this.hasAddPodModalTarget) return

    this.addPodModalTarget.classList.remove("hidden")
    this.addPodModalTarget.setAttribute("aria-hidden", "false")
    this.addPodModalTarget.focus()
    this.updateBodyScrollLock()
  }

  closeAddPodModal(event) {
    if (!this.hasAddPodModalTarget) return

    this.addPodModalTarget.classList.add("hidden")
    this.addPodModalTarget.setAttribute("aria-hidden", "true")
    this.updateBodyScrollLock()
  }

  closeAddPodModalOnBackdrop(event) {
    if (event.target === this.addPodModalTarget) {
      this.closeAddPodModal()
    }
  }

  clearSelection() {
    this.itemTargets.forEach((el) => {
      el.classList.remove("ring-2", "ring-primary", "border-primary")
    })

    this.selectedPagePodId = null
    this.resetInspectorDirtyState()

    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    if (this.hasInspectorFrameTarget) this.inspectorFrameTarget.removeAttribute("src")
    this.closeInspectorDrawer()
  }

  handleInspectorFrameLoad() {
    this.markInspectorClean()
    this.focusFirstInspectorField()
  }

  markInspectorDirty() {
    this.inspectorDirty = true
  }

  markInspectorClean() {
    this.inspectorDirty = false
  }

  resetInspectorDirtyState() {
    this.inspectorDirty = false
  }

  currentInspectorDirty() {
    if (this.hasInspectorFrameTarget) {
      const stateElement = this.inspectorFrameTarget.querySelector("[data-page-override-dirty]")
      if (stateElement) return stateElement.dataset.pageOverrideDirty === "true"
    }

    return this.inspectorDirty
  }

  confirmDiscardIfDirty() {
    if (!this.currentInspectorDirty()) return true

    const shouldDiscard = window.confirm("Discard unsaved changes?")
    if (!shouldDiscard) return false

    this.resetInspectorDirtyState()
    if (this.hasInspectorFrameTarget) this.inspectorFrameTarget.removeAttribute("src")
    return true
  }

  focusFirstInspectorField() {
    if (!this.hasInspectorFrameTarget) return

    const firstField = this.inspectorFrameTarget.querySelector(
      "input:not([type='hidden']):not([disabled]), textarea:not([disabled]), select:not([disabled])"
    )
    if (firstField) firstField.focus()
  }

  openInspectorDrawer() {
    if (this.hasInspectorBackdropTarget) this.inspectorBackdropTarget.classList.remove("hidden")
    if (this.hasInspectorDrawerTarget) this.inspectorDrawerTarget.classList.remove("translate-x-full")
    this.updateBodyScrollLock()
  }

  closeInspectorDrawer() {
    if (this.hasInspectorBackdropTarget) this.inspectorBackdropTarget.classList.add("hidden")
    if (this.hasInspectorDrawerTarget) this.inspectorDrawerTarget.classList.add("translate-x-full")
    this.updateBodyScrollLock()
  }

  requestCloseInspector(event) {
    if (event) event.preventDefault()
    if (!this.inspectorOpen()) return true
    if (!this.confirmDiscardIfDirty()) return false

    this.closeInspectorDrawer()
    return true
  }

  closeInspectorOnBackdrop(event) {
    if (!this.hasInspectorBackdropTarget) return
    if (event.target === this.inspectorBackdropTarget) this.requestCloseInspector()
  }

  handleEscCloseInspector() {
    if (this.addPodModalOpen()) {
      this.closeAddPodModal()
      return
    }

    if (this.inspectorOpen()) this.requestCloseInspector()
  }

  addPodModalOpen() {
    return this.hasAddPodModalTarget && !this.addPodModalTarget.classList.contains("hidden")
  }

  inspectorOpen() {
    return this.hasInspectorDrawerTarget && !this.inspectorDrawerTarget.classList.contains("translate-x-full")
  }

  updateBodyScrollLock() {
    if (this.addPodModalOpen() || this.inspectorOpen()) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
  }
}
